import { Injectable, computed, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface AppConfig {
  inputStyle: string;
  colorScheme: string;
  theme: string;
  ripple: boolean;
  menuMode: string;
  scale: number;
  darkTheme?: boolean;
}

interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  profileSidebarVisible: boolean;
  configSidebarVisible: boolean;
  staticMenuMobileActive: boolean;
  menuHoverActive: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  _config: AppConfig = {
    ripple: false,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: 'light',
    theme: 'saga-orange',
    scale: 14,
  };

  config = signal<AppConfig>(this._config);

  private getInitialSidebarCollapsed(): boolean {
    try {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  }

  state: LayoutState = {
    staticMenuDesktopInactive: this.getInitialSidebarCollapsed(),
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
  };

  private configUpdate = new Subject<AppConfig>();

  private overlayOpen = new Subject<any>();

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  isDarkTheme = computed(() => this.config().darkTheme);

  toggleDarkMode(config?: AppConfig): void {
    const _config = config || this.config();
    localStorage.setItem('theme', _config.theme);
    localStorage.setItem('colorScheme', _config.colorScheme);
    localStorage.setItem('darkTheme', _config.darkTheme ? 'true' : 'false');
    const daisyTheme = _config.colorScheme === 'dark' || _config.darkTheme ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', daisyTheme);
  }

  setSidebarCollapsed(collapsed: boolean): void {
    this.state.staticMenuDesktopInactive = collapsed;
    try {
      localStorage.setItem('sidebarCollapsed', collapsed ? 'true' : 'false');
    } catch {
      // Ignore if localStorage is disabled or inaccessible
    }
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;
      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.setSidebarCollapsed(!this.state.staticMenuDesktopInactive);
    } else {
      this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  showProfileSidebar() {
    this.state.profileSidebarVisible = !this.state.profileSidebarVisible;
    if (this.state.profileSidebarVisible) {
      this.overlayOpen.next(null);
    }
  }

  showConfigSidebar() {
    this.state.configSidebarVisible = true;
  }

  isOverlay() {
    return this.config().menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth >= 1024;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this._config = { ...this.config() };
    this.configUpdate.next(this.config());
  }

  constructor() {
    this.config.update((config) => ({
      ...config,
      theme: localStorage.getItem('theme') || this._config.theme,
      colorScheme: localStorage.getItem('colorScheme') || this._config.colorScheme,
      darkTheme: (localStorage.getItem('darkTheme') === 'true' ) ? true : false  || this._config.colorScheme === 'dark',
    }));
    effect(() => {
      const config = this.config();
      this.changeTheme();
      this.changeScale(config.scale);
      this.onConfigUpdate();
      this.toggleDarkMode(config);
    });
  }

  changeTheme() {
    this.changeThemeById('theme-css');
    this.changeCalendarTheme();
  }

  changeCalendarTheme(): void {
    const head = document.head || document.getElementsByTagName('head')[0];
    const linkElementId = 'calendar-css';
    const existingLinkElement = document.getElementById(linkElementId) as HTMLLinkElement;

    if (this.config().colorScheme == 'dark') {
      if (!existingLinkElement) {
        const linkElement = document.createElement('link');
        linkElement.id = linkElementId;
        linkElement.rel = 'stylesheet';
        linkElement.type = 'text/css';
        linkElement.href = 'assets/layout/styles/calendar/calendar-dark.css';
        head.appendChild(linkElement);
      }
    } else {
      if (existingLinkElement) {
        head.removeChild(existingLinkElement);
      }
    }
  }

  changeThemeById(elementId: string) {
    const config = this.config();
    const themeLink = <HTMLLinkElement>document.getElementById(elementId);
    const themeLinkHref = themeLink.getAttribute('href')!;
    const newHref = themeLinkHref
      .split('/')
      .map((el) =>
        el == this._config.theme
          ? (el = config.theme)
          : el == `theme-${this._config.colorScheme}`
          ? (el = `theme-${config.colorScheme}`)
          : el,
      )
      .join('/');

    this.replaceThemeLink(newHref);
  }

  replaceThemeLink(href: string) {
    const id = 'theme-css';
    let themeLink = <HTMLLinkElement>document.getElementById(id);
    const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', id + '-clone');

    themeLink.parentNode!.insertBefore(cloneLinkElement, themeLink.nextSibling);
    cloneLinkElement.addEventListener('load', () => {
      themeLink.remove();
      cloneLinkElement.setAttribute('id', id);
    });
  }

  changeScale(value: number) {
    document.documentElement.style.setProperty('--app-scale', `${value}px`);
    document.documentElement.style.removeProperty('font-size');
  }
}
